// src/components/resources/ebooks/EbookViewerWrapper.tsx

'use client'

import { useState, useCallback, useMemo, type ReactNode } from 'react'
import Link from 'next/link'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'
import { isEbook, type Ebook } from '~/lib/resources/types/ebooks'
import type { RelatedResource } from '~/lib/resources/types/shared/base'
import { getEbookCoverTheme } from '~/lib/resources/data/ebooks/themes'
import { EbookCover } from '~/components/resources/ebooks/EbookCover'
import { EbookContentBlocks } from '~/components/resources/ebooks/EbookContentBlocks'
import { EbookViewer } from '~/components/resources/ebooks/EbookViewer'
import { FAQ } from '~/components/shared/FAQ'
import {
  ArrowDownTrayIcon,
  ShareIcon,
  ClockIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BookOpenIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

interface EbookViewerWrapperProps {
  ebook: Ebook
}

function FormattedContent({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)

  if (lines.length > 1) {
    return (
      <ul className="space-y-3">
        {lines.map((l, i) => (
          <li key={i} className="flex gap-3 text-gray-600 dark:text-gray-300">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" aria-hidden="true" />
            <span className="leading-relaxed">{l.replace(/^[-•]\s*/, '')}</span>
          </li>
        ))}
      </ul>
    )
  }

  return <p className="leading-relaxed text-gray-600 dark:text-gray-300">{lines[0] ?? ''}</p>
}

function Section({ title, children, id }: { title: string; children: ReactNode; id?: string }): JSX.Element {
  return (
    <section id={id} className="scroll-mt-28 space-y-4">
      <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">{title}</h2>
      {children}
    </section>
  )
}

function MetaChip({ icon: Icon, children }: { icon: typeof ClockIcon; children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
      <Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      {children}
    </span>
  )
}

export function EbookViewerWrapper({ ebook }: EbookViewerWrapperProps): JSX.Element {
  const valid = isEbook(ebook)
  const downloadConfig = valid ? ebook.config?.download : undefined
  const isGated = Boolean(downloadConfig?.isGated)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [accessGranted, setAccessGranted] = useState(!isGated)
  const [copied, setCopied] = useState(false)

  // Cache Components: the gated email-capture modal renders through a Radix
  // <Dialog> PORTAL (attached to document.body), so it escapes the route's
  // subtree — Activity's `display:none` on the hidden route would NOT hide it,
  // leaving the modal (and its post-submit "check your inbox" success state)
  // floating over the next page on back-/forward-nav. Close it when this route
  // is hidden (and on unmount). Mirrors the quiz/persona modal resets.
  useIsomorphicLayoutEffect(() => {
    return () => setShowEmailCapture(false)
  }, [])

  const coverTheme = valid ? getEbookCoverTheme(ebook.category) : getEbookCoverTheme()
  const difficultyLabel = valid && ebook.difficulty
    ? ebook.difficulty.charAt(0).toUpperCase() + ebook.difficulty.slice(1)
    : null

  const relatedLinks = useMemo(
    () =>
      valid
        ? (ebook.relatedResources ?? []).filter(
            (r): r is RelatedResource => typeof r !== 'string' && Boolean(r.fullRoute),
          )
        : [],
    [ebook.relatedResources, valid],
  )

  const interactiveTool = useMemo(
    () =>
      relatedLinks.find(
        (r) => r.resourceType === 'tool' && (r.toolType === 'calculator' || r.toolType === 'generator'),
      ),
    [relatedLinks],
  )

  const handleDownload = useCallback(() => {
    if (!downloadConfig) return
    if (downloadConfig.isGated && !accessGranted) {
      setShowEmailCapture(true)
      return
    }
    window.open(downloadConfig.contentUrl, '_blank')
  }, [downloadConfig, accessGranted])

  const handleShare = useCallback(async () => {
    if (!valid) return
    try {
      await navigator.share({ title: ebook.title, url: window.location.href })
    } catch {
      try {
        await navigator.clipboard.writeText(window.location.href)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch {
        /* no-op */
      }
    }
  }, [ebook.title, valid])

  if (!valid) {
    return <p className="p-8 text-center text-gray-500">Ebook not found.</p>
  }

  const ctaLabel = isGated && !accessGranted ? 'Email me the eBook' : 'Download PDF'

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 sm:px-6 lg:px-8">
      {/* Hero — book cover + metadata */}
      <header className="relative overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-blue-50/40 p-6 dark:border-gray-800 dark:from-gray-900 dark:via-gray-950 dark:to-blue-950/20 sm:p-10">
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl dark:bg-blue-500/10"
          aria-hidden="true"
        />
        <div className="relative grid gap-10 lg:grid-cols-[auto,minmax(0,1fr)] lg:items-center">
          <div className="mx-auto lg:mx-0">
            <EbookCover
              title={ebook.title}
              category={ebook.category}
              author={ebook.author}
              pageCount={ebook.pageCount}
              size="xl"
              elevated
            />
          </div>

          <div className="space-y-5 text-center lg:text-left">
            <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
              <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                Free eBook
              </span>
              <span className="rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-300">
                {coverTheme.label}
              </span>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-[2.5rem] lg:leading-tight">
              {ebook.title}
            </h1>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300 lg:mx-0">
              {ebook.description}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-2.5 lg:justify-start">
              {ebook.estimatedTime && <MetaChip icon={ClockIcon}>{ebook.estimatedTime}</MetaChip>}
              {difficultyLabel && <MetaChip icon={ChartBarIcon}>{difficultyLabel}</MetaChip>}
              {ebook.pageCount && <MetaChip icon={DocumentTextIcon}>{ebook.pageCount} pages</MetaChip>}
              {ebook.author && <MetaChip icon={BookOpenIcon}>{ebook.author}</MetaChip>}
            </div>

            <div className="flex flex-wrap justify-center gap-3 pt-1 lg:justify-start">
              <button
                onClick={handleDownload}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-blue-500 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:bg-blue-500 dark:hover:bg-blue-400"
              >
                <ArrowDownTrayIcon className="h-5 w-5" /> {ctaLabel}
              </button>
              <button
                onClick={handleShare}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
              >
                <ShareIcon className="h-5 w-5" /> {copied ? 'Link copied!' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Interactive companion callout */}
      {interactiveTool && (
        <Link
          href={interactiveTool.fullRoute ?? '#'}
          className="mt-8 flex items-center justify-between gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-5 transition-colors hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
              <SparklesIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <span>
              <span className="block font-semibold text-gray-900 dark:text-white">
                Put the playbook into practice
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-300">
                Try the {interactiveTool.title} while you read.
              </span>
            </span>
          </span>
          <ArrowRightIcon className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
        </Link>
      )}

      {/* Body + sticky sidebar */}
      <div className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,1fr),300px]">
        <article className="min-w-0 space-y-12">
          {ebook.sections?.map((section) => (
            <Section key={section.id} title={section.title}>
              <FormattedContent text={section.content} />
            </Section>
          ))}

          {ebook.chapters.length > 0 && (
            <Section title="Inside the eBook" id="chapters">
              <div className="relative space-y-0">
                {ebook.chapters.map((chapter, index) => (
                  <div key={chapter.order} className="relative flex gap-5 pb-8 last:pb-0">
                    {index < ebook.chapters.length - 1 && (
                      <div
                        className="absolute left-[18px] top-10 bottom-0 w-px bg-gradient-to-b from-blue-200 to-transparent dark:from-blue-800"
                        aria-hidden="true"
                      />
                    )}
                    <div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white shadow-md ring-4 ring-white dark:ring-gray-950">
                      {chapter.order}
                    </div>
                    <div className="min-w-0 flex-1 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-900/60">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{chapter.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                        {chapter.description}
                      </p>
                      {chapter.sections[0]?.blocks && chapter.sections[0].blocks.length > 0 && (
                        <EbookContentBlocks blocks={chapter.sections[0].blocks} limit={2} />
                      )}
                      {chapter.sections[0]?.keyTakeaways && (
                        <ul className="mt-4 space-y-2 border-t border-gray-100 pt-4 dark:border-gray-800">
                          {chapter.sections[0].keyTakeaways.map((point) => (
                            <li key={point} className="flex gap-2 text-sm text-gray-500 dark:text-gray-400">
                              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-blue-400" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Each chapter includes formulas, benchmark tables, worked scenarios, and checklists in the full PDF.
              </p>
            </Section>
          )}

          {ebook.topics && ebook.topics.length > 0 && (
            <Section title="Topics Covered">
              <div className="flex flex-wrap gap-2">
                {ebook.topics.map((topic) => (
                  <span
                    key={topic}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-medium capitalize text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-200"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {ebook.faqs && ebook.faqs.length > 0 && (
            <Section title="Frequently Asked Questions">
              <FAQ type="single" features={{ showTitle: false }} items={ebook.faqs} />
            </Section>
          )}
        </article>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="space-y-6">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className={cnCoverHeader(coverTheme.gradient)}>
                <p className="text-xs font-semibold uppercase tracking-wider text-white/80">Download</p>
                <p className="mt-1 text-lg font-bold text-white">Full PDF eBook</p>
              </div>
              <div className="space-y-4 p-6">
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  {ebook.pageCount && (
                    <li className="flex items-center gap-2">
                      <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                      {ebook.pageCount} pages · {ebook.fileSize ?? 'PDF'}
                    </li>
                  )}
                  {ebook.estimatedTime && (
                    <li className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4 text-gray-400" />
                      {ebook.estimatedTime}
                    </li>
                  )}
                </ul>
                <button
                  onClick={handleDownload}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-400"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" /> {ctaLabel}
                </button>
                <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                  {isGated ? 'Free — we will email you the eBook download link.' : 'Free instant download.'}
                </p>
              </div>
            </div>

            {ebook.tableOfContents && ebook.tableOfContents.entries.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Table of contents</h3>
                <ol className="mt-4 space-y-2.5">
                  {ebook.tableOfContents.entries.map((entry) => (
                    <li
                      key={entry.title}
                      className="flex items-baseline justify-between gap-3 text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="font-medium text-gray-800 dark:text-gray-200">{entry.title}</span>
                      {entry.page && <span className="shrink-0 tabular-nums text-gray-400">p. {entry.page}</span>}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {relatedLinks.length > 0 && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Related resources</h3>
                <ul className="mt-4 space-y-3">
                  {relatedLinks.slice(0, 5).map((ref) => (
                    <li key={ref.fullRoute ?? ref.id}>
                      <Link
                        href={ref.fullRoute ?? '#'}
                        className="group flex items-start gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        <ArrowRightIcon className="mt-0.5 h-4 w-4 shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                        <span>{ref.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Bottom CTA band */}
      <div className="mt-16 overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-10 text-center text-white shadow-lg">
        <h2 className="text-2xl font-bold">Ready to dive in?</h2>
        <p className="mx-auto mt-2 max-w-lg text-blue-100">
          Get the complete{ebook.pageCount ? ` ${ebook.pageCount}-page` : ''} playbook delivered to your inbox — free.
        </p>
        <button
          onClick={handleDownload}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-blue-700 shadow transition-colors hover:bg-blue-50"
        >
          <ArrowDownTrayIcon className="h-5 w-5" /> {ctaLabel}
        </button>
      </div>

      <EbookViewer
        ebook={ebook}
        showEmailCapture={showEmailCapture}
        onCloseEmailCapture={() => setShowEmailCapture(false)}
        accessGranted={accessGranted}
        onEmailSuccess={() => {
          // Deliver the eBook BY EMAIL only — never auto-open the PDF. Lead
          // capture is the point; the modal stays open to show the
          // "check your inbox" confirmation, and a manual Download button
          // remains available for anyone who'd rather grab it on-page.
          setAccessGranted(true)
        }}
      />
    </div>
  )
}

/** Sidebar card header gradient from theme tokens */
function cnCoverHeader(gradient: string): string {
  return `bg-gradient-to-br ${gradient} px-6 py-5`
}
